/**
 * 未登録のBB ID の登録(D-206)。オペレーターマスタの「未登録のBB ID」から開く。
 *  - 名前が同じスタッフがいれば、その人の旧ID(無効)として結び付ける(既定)
 *  - 別のスタッフを選んで結び付けることもできる
 *  - だれにも当たらないときだけ、今までどおり新しい人として仮登録する
 * 結び付けても過去の日次集計は変わらない(取込のときに作るため)。その期間を取り込み直す案内を出す。
 */
import { useMemo, useState } from 'react';
import Modal from '@core/components/Modal';
import { BUSINESS_LABELS, PAY_TYPE_LABELS, WORK_STYLE_LABELS, type Operator } from '@core/lib/types';
import { linkCandidates, oldAccountFor } from './unknownBbLink';
import { guessFromBbId, type UnknownBbId } from './unknownBbRepo';

export type UnknownBbChoice = { kind: 'link'; operator: Operator } | { kind: 'provisional' } | { kind: 'ignore' };

export default function UnknownBbLinkModal({
  unknown,
  operators,
  busy,
  onClose,
  onSubmit,
}: {
  unknown: UnknownBbId;
  operators: Operator[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (choice: UnknownBbChoice) => void;
}) {
  const candidates = useMemo(() => linkCandidates(unknown, operators), [unknown, operators]);
  const others = useMemo(
    () => operators.filter((o) => !o.merged_into && !candidates.includes(o)).sort((a, b) => Number(b.status === 'active') - Number(a.status === 'active') || a.name.localeCompare(b.name, 'ja')),
    [operators, candidates],
  );
  const [mode, setMode] = useState<string>(candidates[0] ? `c:${candidates[0].id}` : 'provisional');
  const [otherId, setOtherId] = useState('');
  const g = guessFromBbId(unknown.bb_id, unknown.name);

  const target = mode.startsWith('c:') ? candidates.find((o) => `c:${o.id}` === mode) : mode === 'other' ? others.find((o) => o.id === otherId) : undefined;
  const account = target ? oldAccountFor(unknown, target) : null;
  const canSubmit = mode === 'provisional' || mode === 'ignore' || !!target;

  return (
    <Modal onClose={busy ? () => undefined : onClose} width="min(640px, 94vw)">
      <h3 className="training-modal-title">
        {unknown.bb_id}
        {unknown.name ? ` ${unknown.name}` : ''} を登録
      </h3>
      <p className="ui-hint">
        {WORK_STYLE_LABELS[g.work_style]}／{PAY_TYPE_LABELS[g.pay_type]}／{BUSINESS_LABELS[g.business]}(BB IDからの推測) ・ 取込に出てきた日 {unknown.first_date} 〜 {unknown.last_date}
      </p>
      <fieldset className="unknown-link-choices" disabled={busy}>
        <legend className="sr-only">登録のしかた</legend>
        {candidates.map((o) => (
          <label key={o.id} className="unknown-link-choice">
            <input type="radio" name="unknown-link" checked={mode === `c:${o.id}`} onChange={() => setMode(`c:${o.id}`)} />
            <span>
              <b>
                {o.op_code} {o.name}
              </b>{' '}
              の旧IDとして結び付ける{o.status !== 'active' && <span className="ui-hint">(在籍ではありません)</span>}
            </span>
          </label>
        ))}
        <label className="unknown-link-choice">
          <input type="radio" name="unknown-link" checked={mode === 'other'} onChange={() => setMode('other')} />
          <span>ほかのスタッフの旧IDとして結び付ける</span>
        </label>
        {mode === 'other' && (
          <select className="ui-input unknown-link-select" aria-label="結び付けるスタッフ" value={otherId} onChange={(e) => setOtherId(e.target.value)}>
            <option value="">スタッフを選ぶ</option>
            {others.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}({o.op_code}){o.status !== 'active' ? ' ・在籍以外' : ''}
              </option>
            ))}
          </select>
        )}
        <label className="unknown-link-choice">
          <input type="radio" name="unknown-link" checked={mode === 'provisional'} onChange={() => setMode('provisional')} />
          <span>新しい人として仮登録する(マスタにいない人のとき)</span>
        </label>
        <label className="unknown-link-choice">
          <input type="radio" name="unknown-link" checked={mode === 'ignore'} onChange={() => setMode('ignore')} />
          <span>登録しない(一覧から外す。このIDの稼働・取得は集計に入りません)</span>
        </label>
      </fieldset>
      {account && (
        <p className="ui-hint">
          無効の旧IDとして足します。時給は{account.wage_pending ? '未設定(あとで入れてください)' : 'その人の同じ種類のIDから写します'}。
          <b>過去の日の数字は、{unknown.first_date} 〜 {unknown.last_date} の OPレポート・対応履歴を取り込み直すと入ります。</b>
        </p>
      )}
      {candidates.length === 0 && mode === 'provisional' && <p className="ui-hint">名前が同じスタッフはマスタにいません。</p>}
      <div className="training-actions">
        <button
          type="button"
          className="ui-btn ui-btn-primary"
          disabled={!canSubmit || busy}
          onClick={() => onSubmit(target ? { kind: 'link', operator: target } : mode === 'ignore' ? { kind: 'ignore' } : { kind: 'provisional' })}
        >
          {busy ? '登録中…' : target ? `${target.name} に結び付ける` : mode === 'ignore' ? '登録しない' : '仮登録する'}
        </button>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose} disabled={busy}>
          やめる
        </button>
      </div>
    </Modal>
  );
}
